import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import JUMonogram from "./JUMonogram";
import { SCROLL, CONTACTS } from "../data/content";

const LINKS = [
    { label: "Home", id: "top" },
    { label: "Packages", id: "packages" },
    { label: "Bespoke", to: "/bespoke" },
    { label: "Destinations", id: "destinations" },
    { label: "How It Works", id: "how-it-works" },
    { label: "Contact", id: "contact" },
];

export default function Nav() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (item) => {
        setOpen(false);
        if (item.to) {
            if (location.pathname !== item.to) navigate(item.to);
            SCROLL.go("top");
        } else if (location.pathname !== "/") {
            navigate("/", { state: { scrollTo: item.id } });
        } else {
            SCROLL.go(item.id);
        }
    };

    return (
        <>
            <header
                className={`fixed inset-x-0 top-0 z-40 transition-all duration-500 ${
                    scrolled
                        ? "bg-navy/95 backdrop-blur-md border-b border-gold/20 py-2"
                        : "bg-gradient-to-b from-navy/70 to-transparent py-4"
                }`}
            >
                <div className="container-luxe flex items-center justify-between">
                    <button
                        data-testid="nav-logo"
                        onClick={() => go(LINKS[0])}
                        className="flex items-center gap-3 text-left"
                        aria-label="Journeys Unscripted — home"
                    >
                        <JUMonogram size={40} />
                        <span className="font-serif text-base sm:text-lg text-cream">
                            Journeys <span className="italic text-gold">Unscripted</span>
                        </span>
                    </button>

                    <nav className="hidden md:flex items-center gap-7">
                        {LINKS.map((l) => (
                            <button
                                key={l.label}
                                data-testid={`nav-link-${(l.to || l.id).replace("/", "")}`}
                                onClick={() => go(l)}
                                className={`text-[12px] tracking-[0.16em] uppercase transition-colors duration-300 ${
                                    l.to && location.pathname === l.to
                                        ? "text-gold"
                                        : "text-cream/85 hover:text-gold"
                                }`}
                            >
                                {l.label}
                            </button>
                        ))}
                        <button
                            data-testid="nav-cta-plan"
                            onClick={() => go(LINKS[5])}
                            className="bg-gold text-navy px-5 py-2.5 text-[11px] tracking-[0.16em] uppercase font-medium transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#b8925a]"
                        >
                            Plan My Journey
                        </button>
                    </nav>

                    <button
                        data-testid="nav-mobile-toggle"
                        className="md:hidden text-cream p-2 -mr-2"
                        onClick={() => setOpen(true)}
                        aria-label="Open menu"
                    >
                        <Menu size={26} strokeWidth={1.25} />
                    </button>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-50 bg-navy flex flex-col"
                    >
                        <div className="container-luxe flex items-center justify-between py-4">
                            <div className="flex items-center gap-3">
                                <JUMonogram size={40} />
                                <span className="font-serif text-base text-cream">
                                    Journeys <span className="italic text-gold">Unscripted</span>
                                </span>
                            </div>
                            <button
                                data-testid="nav-mobile-close"
                                className="text-cream p-2 -mr-2"
                                onClick={() => setOpen(false)}
                                aria-label="Close menu"
                            >
                                <X size={26} strokeWidth={1.25} />
                            </button>
                        </div>
                        <nav className="flex-1 flex flex-col justify-center px-8 sm:px-12">
                            {LINKS.map((l, i) => (
                                <motion.button
                                    key={l.label}
                                    data-testid={`nav-mobile-${(l.to || l.id).replace("/", "")}`}
                                    initial={{ opacity: 0, x: 24 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.07 * i, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                                    onClick={() => go(l)}
                                    className="text-left font-serif text-3xl text-cream py-3.5 border-b border-gold/15 flex items-baseline justify-between"
                                >
                                    {l.label}
                                    {l.to && <span className="text-xs italic text-gold tracking-[0.2em] uppercase">HIT but HINT</span>}
                                </motion.button>
                            ))}
                        </nav>
                        <div className="px-8 sm:px-12 pb-10">
                            <p className="eyebrow mb-3">Speak with us</p>
                            {CONTACTS.map((c) => (
                                <a
                                    key={c.name}
                                    data-testid={`nav-mobile-call-${c.name.toLowerCase()}`}
                                    href={`tel:${c.tel}`}
                                    className="block font-serif text-cream/90 text-lg py-1"
                                >
                                    {c.name} · {c.phoneDisplay}
                                </a>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
